/**
 * VoiceCreateModals — the canonical "Clone your voice" and "Design a voice"
 * flows. Extracted from the Assets page so the SAME modal opens whether the
 * user starts from Assets or from the agent chatbox's voice menu (no redirect
 * to /assets). Keep this the single source of truth for voice creation.
 */
import { useEffect, useRef, useState } from 'react';
import { trackAmpVoiceCreated } from '@/services/amplitudeService';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { X, Mic, Upload, Play, Square, Loader2, Check } from 'lucide-react';
import { cn } from '@/v2/utils';
import { apiService } from '@/api/apiService';
import { v2Service } from '@/api/v2Service';
import { isFreeLimitError, isCreditDeclined, useUpgradeModal } from '@/v2/upgradeModal';
import { isLongvidProvider } from '@/api/providerEndpoints';
import { useUserStore } from '@/store/userStore';
import { togglePreview, isPreviewPlaying, isPreviewLoading, onPreviewChange } from '@/v2/audio';
import { toast } from '@/v2/toast';
import i18n from '@/i18n';
import type { V2Voice, V2CloneVoiceResult } from '@/api/v2Types';

const inputCls =
  'w-full bg-surface-light border border-glass-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary';

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div
        className={cn('w-full rounded-2xl bg-surface border border-glass-border p-5 shadow-2xl animate-slide-up max-h-[85vh] overflow-y-auto', wide ? 'max-w-2xl' : 'max-w-md')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-text-primary">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-surface-light flex items-center justify-center text-text-secondary">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

const getCloneScript = () => i18n.t('voiceCreate.cloneScript', { ns: 'v2' });

// A LongVid-routed user's voice is cloned by LongVid, which refuses a sample with
// less speech than this — so the modal says so before anything is spent.
const MIN_CLONE_SECS = 10;

/** The sample's length in seconds, or 0 when the browser cannot tell. */
function audioDurationSec(sample: Blob): Promise<number> {
  return new Promise((resolve) => {
    let url = '';
    try { url = URL.createObjectURL(sample); } catch { resolve(0); return; }
    const el = new Audio();
    const done = (secs: number) => { URL.revokeObjectURL(url); resolve(secs); };
    el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? Math.floor(el.duration) : 0);
    el.onerror = () => done(0);
    el.src = url;
  });
}

function ScriptPanel({ recording }: { recording?: boolean }) {
  const { t } = useTranslation('v2');
  const ref = useRef<HTMLDivElement>(null);
  const cloneScript = getCloneScript();

  // While recording, scroll the script slowly at a comfortable reading pace
  // (~140 wpm) so the user never has to scroll mid-take. The moment they
  // scroll themselves (wheel / touch), the auto-scroll backs off for good.
  useEffect(() => {
    if (!recording) return;
    const el = ref.current;
    if (!el) return;
    el.scrollTop = 0;
    let stopped = false;
    const stop = () => { stopped = true; };
    el.addEventListener('wheel', stop, { passive: true });
    el.addEventListener('touchmove', stop, { passive: true });
    const words = cloneScript.split(/\s+/).length;
    const readMs = (words / (140 / 60)) * 1000;
    const startDelay = 4000; // the opening lines are visible without scrolling
    const t0 = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      if (stopped) return;
      const max = el.scrollHeight - el.clientHeight;
      const p = Math.min(1, Math.max(0, (t - t0 - startDelay) / readMs));
      el.scrollTop = max * p;
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('wheel', stop);
      el.removeEventListener('touchmove', stop);
    };
  }, [recording]);

  return (
    <div
      ref={ref}
      className={cn(
        'rounded-xl bg-white/[0.04] border border-glass-border overflow-y-auto',
        recording ? 'max-h-[48vh] p-5' : 'max-h-72 p-4'
      )}
    >
      <p className="text-[10px] font-bold uppercase tracking-widest text-text-tertiary mb-2">{t('voiceCreate.readOutLoud')}</p>
      <p className={cn(
        'whitespace-pre-line',
        recording ? 'text-lg leading-relaxed text-text-primary' : 'text-sm leading-relaxed text-text-secondary'
      )}>
        {cloneScript}
      </p>
    </div>
  );
}

export function CloneVoiceModal({ onClose, onDone, onCreated }: { onClose: () => void; onDone?: () => void; onCreated?: (r: V2CloneVoiceResult) => void }) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const isPaid = useUserStore((s) => s.isPro());
  // LongVid clones the voice itself for its own users, and wants at least
  // MIN_CLONE_SECS of speech. Every other user's modal is exactly as it was.
  const videoProvider = useUserStore((s) => s.user?.videoProvider);
  const isLongvid = isLongvidProvider(videoProvider);
  const [name, setName] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [sampleSecs, setSampleSecs] = useState(0);   // 0 = the length is not known here
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // --- On-site mic recording (MediaRecorder) ---
  const [recState, setRecState] = useState<'idle' | 'prep' | 'countdown' | 'recording' | 'recorded'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [recSecs, setRecSecs] = useState(0);
  const [recBlobUrl, setRecBlobUrl] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recBlobRef = useRef<Blob | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTracks = () => recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
  useEffect(() => () => { // cleanup on close: stop mic + timer
    if (timerRef.current) clearInterval(timerRef.current);
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    stopTracks();
  }, []);

  const startRecording = async () => {
    setError(null);
    // Bail early on browsers without recording support (some embedded webviews).
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setRecState('prep');
      setError(t('voiceCreate.errBrowserNoRecord'));
      return;
    }
    // Mic permission — its OWN try so a real "blocked" only shows for an actual denial.
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setRecState('prep');
      setError(t('voiceCreate.errMicBlocked'));
      return;
    }
    // 3-2-1 countdown so the user can take a breath and find the first line.
    setRecState('countdown');
    for (const n of [3, 2, 1]) {
      setCountdown(n);
      await new Promise((r) => setTimeout(r, 1000));
    }
    // Build the recorder. Desktop Chrome records audio/webm; iOS Safari records audio/mp4 and
    // will THROW on an explicit {mimeType:'audio/mp4'} even when isTypeSupported() returns true
    // — so try a supported type but ALWAYS fall back to the browser default. (That throw was the
    // iPhone bug: it landed in the old single catch and looked like "mic blocked".)
    let rec: MediaRecorder | null = null;
    for (const t of ['audio/webm', 'audio/mp4']) {
      if (rec) break;
      try { if (MediaRecorder.isTypeSupported?.(t)) rec = new MediaRecorder(stream, { mimeType: t }); }
      catch { /* not usable on this device — try the next candidate */ }
    }
    if (!rec) { try { rec = new MediaRecorder(stream); } catch { rec = null; } } // default container
    if (!rec) {
      stream.getTracks().forEach((t) => t.stop());
      setRecState('prep');
      setError(t('voiceCreate.errBrowserNoRecordHere'));
      return;
    }
    const recorder = rec;
    chunksRef.current = [];
    recorder.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/mp4' });
      recBlobRef.current = blob;
      setRecBlobUrl(URL.createObjectURL(blob));
      setRecState('recorded');
      stream.getTracks().forEach((t) => t.stop());
    };
    recorderRef.current = recorder;
    try {
      recorder.start();
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      setRecState('prep');
      setError(t('voiceCreate.errCouldntStart'));
      return;
    }
    setRecSecs(0);
    setRecState('recording');
    timerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    recorderRef.current?.stop();
  };

  const discardRecording = () => {
    if (recBlobUrl) URL.revokeObjectURL(recBlobUrl);
    recBlobRef.current = null;
    setRecBlobUrl(null);
    setRecSecs(0);
    setRecState('idle');
  };

  const useRecording = async () => {
    const blob = recBlobRef.current;
    if (!blob || !userId) return;
    setUploading(true); setError(null);
    try {
      const ext = blob.type.includes('mp4') ? 'm4a' : 'webm';
      const { uploadUrl, publicUrl } = await apiService.getUploadUrl(userId, `voice-recording.${ext}`, blob.type);
      await apiService.uploadFile(new File([blob], `voice-recording.${ext}`, { type: blob.type }), uploadUrl, blob.type);
      setSampleSecs(recSecs);   // the take's own timer, already counted second by second
      setAudioUrl(publicUrl);
    } catch {
      setError(t('voiceCreate.errUploadRecording'));
    } finally { setUploading(false); }
  };

  const upload = async (f: File | undefined) => {
    if (!f || !userId) return;
    setUploading(true); setError(null);
    try {
      const type = f.type || 'audio/mpeg';
      const { uploadUrl, publicUrl } = await apiService.getUploadUrl(userId, f.name, type);
      await apiService.uploadFile(f, uploadUrl, type);
      // Measured alongside, never in the way: a browser that cannot read the metadata
      // leaves the length unknown and the server still has the last word.
      void audioDurationSec(f).then(setSampleSecs);
      setAudioUrl(publicUrl);
    } catch {
      setError(t('voiceCreate.errUploadFailed'));
    } finally { setUploading(false); }
  };

  // Known to be under LongVid's floor: the CTA is held rather than spending a paid
  // clone on a sample that will be refused. An unknown length never blocks.
  const tooShort = isLongvid && sampleSecs > 0 && sampleSecs < MIN_CLONE_SECS;

  const clone = async () => {
    if (!userId || !name.trim() || !audioUrl || !consent || tooShort) return;
    // Premium gating: cloning a voice is paid-only → redirect free users to
    // upgrade at the CTA.
    if (!isPaid) { useUpgradeModal.getState().show({ kind: 'free_asset_limit', asset: 'voice' }); return; }
    setBusy(true); setError(null);
    try {
      const r = await v2Service.cloneVoice({
        userId, voiceName: name.trim(), s3Url: audioUrl, consent,
        durationSec: sampleSecs || undefined,
      });
      trackAmpVoiceCreated({ method: 'clone' });
      onCreated?.(r);
      onDone?.(); onClose();
      toast.info(t('voiceCreate.toastCloningStarted'));
    } catch (e) {
      if (isFreeLimitError(e) || isCreditDeclined(e)) { onClose(); return; } // popup / user cancel
      const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(
        code === 'sample_too_short' ? t('voiceCreate.errSampleTooShort', { seconds: MIN_CLONE_SECS })
          : code === 'voice_service_unavailable' ? t('voiceCreate.errCloneUnavailable')
            : t('voiceCreate.errCloningFailed')
      );
      toast.error(t('voiceCreate.toastCloneStartFailed'));
    } finally { setBusy(false); }
  };

  const mmss = `${String(Math.floor(recSecs / 60)).padStart(2, '0')}:${String(recSecs % 60).padStart(2, '0')}`;

  return (
    <Modal title={t('voiceCreate.cloneTitle')} onClose={onClose} wide>
      <div className="space-y-3">
        <input className={inputCls} placeholder={t('voiceCreate.voiceNamePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} />
        <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => void upload(e.target.files?.[0])} />

        {audioUrl ? (
          /* Final sample (recorded or uploaded) */
          <div className="flex items-center gap-3">
            <audio controls src={audioUrl} className="h-9 flex-1" />
            <button onClick={() => { setAudioUrl(''); setSampleSecs(0); discardRecording(); }} className="text-xs text-text-tertiary hover:text-text-primary">{t('voiceCreate.remove')}</button>
          </div>
        ) : recState === 'prep' ? (
          /* Get ready: read the script, then start with a countdown */
          <div className="space-y-3">
            <ScriptPanel />
            <div className="flex gap-2">
              <button onClick={() => void startRecording()}
                className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-95 transition-opacity">
                {t('voiceCreate.imReadyStart')}
              </button>
              <button onClick={() => setRecState('idle')} className="px-4 py-2.5 rounded-full glass text-sm text-text-secondary hover:text-text-primary">
                {t('voiceCreate.back')}
              </button>
            </div>
          </div>
        ) : recState === 'countdown' ? (
          /* 3‑2‑1 before the mic goes live */
          <div className="rounded-xl border border-primary/40 bg-primary/5 p-6 text-center">
            <p className="text-5xl font-extrabold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent tabular-nums animate-pulse">{countdown}</p>
            <p className="text-xs text-text-tertiary mt-2">{t('voiceCreate.takeABreath')}</p>
          </div>
        ) : recState === 'recording' ? (
          /* Live recording — script stays on screen to read along */
          <div className="rounded-xl border border-red-400/40 bg-red-500/5 p-4 space-y-3">
            <p className="flex items-center justify-center gap-2 text-sm font-semibold text-text-primary">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> {t('voiceCreate.recordingTime', { time: mmss })}
            </p>
            <ScriptPanel recording />
            <div className="text-center">
              <button onClick={stopRecording} className="px-5 py-2 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-400 transition-colors">
                {t('voiceCreate.stopRecording')}
              </button>
            </div>
          </div>
        ) : recState === 'recorded' && recBlobUrl ? (
          /* Review the take */
          <div className="rounded-xl border border-glass-border bg-surface-light/40 p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs text-text-secondary">
              <span className="font-semibold text-text-primary">{t('voiceCreate.yourRecording')}</span> · {mmss}
              {recSecs < 30 && <span className="text-amber-300/80">{t('voiceCreate.under30s')}</span>}
            </div>
            <audio controls src={recBlobUrl} className="h-9 w-full" />
            <div className="flex gap-2">
              <button onClick={() => void useRecording()} disabled={uploading}
                className="flex-1 py-2 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold disabled:opacity-50">
                {uploading ? t('voiceCreate.uploading') : t('voiceCreate.useThisRecording')}
              </button>
              <button onClick={() => { discardRecording(); setRecState('prep'); }} className="px-4 py-2 rounded-full glass text-sm text-text-secondary hover:text-text-primary">
                {t('voiceCreate.tryAgain')}
              </button>
            </div>
          </div>
        ) : (
          /* Choose: record on-site or upload a file */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => setRecState('prep')}
              className="flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 border-dashed border-glass-border text-text-tertiary hover:border-primary/50 hover:text-primary transition-colors"
            >
              <Mic className="w-6 h-6" />
              <span className="text-sm font-semibold">{t('voiceCreate.recordNow')}</span>
              <span className="text-[11px]">{t('voiceCreate.recordNowHint')}</span>
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 border-dashed border-glass-border text-text-tertiary hover:border-primary/50 hover:text-primary transition-colors disabled:opacity-50"
            >
              <Upload className="w-6 h-6" />
              <span className="text-sm font-semibold">{uploading ? t('voiceCreate.uploading') : t('voiceCreate.uploadAFile')}</span>
              <span className="text-[11px]">{t('voiceCreate.cleanSpeechHint')}</span>
            </button>
          </div>
        )}

        <label className="flex items-start gap-2 text-xs text-text-secondary">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          {t('voiceCreate.consentLabel')}
        </label>
        {tooShort && <p className="text-xs text-amber-300/80">{t('voiceCreate.errSampleTooShort', { seconds: MIN_CLONE_SECS })}</p>}
        {error && <p className="text-xs text-amber-300/80">{error}</p>}
        <button
          onClick={() => void clone()}
          disabled={busy || !name.trim() || !audioUrl || !consent || tooShort}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold disabled:opacity-50"
        >
          {busy ? t('voiceCreate.cloningEllipsis') : t('voiceCreate.cloneVoice')}
        </button>
      </div>
    </Modal>
  );
}

// Prompts VERIFIED to return designed voices from HeyGen's search (the search is picky, so
// these reliably match). Picking one autofills the prompt box; the user can still edit it.
const SAMPLE_PROMPTS: { labelKey: string; prompt: string }[] = [
  { labelKey: 'voiceCreate.sampleWarmFemaleNarrator', prompt: 'mature female voice, American accent, warm and friendly, measured pacing, good for narration' },
  { labelKey: 'voiceCreate.sampleSoothingFemaleMeditation', prompt: 'mature female voice, soft and soothing, calm pacing, good for meditation' },
  { labelKey: 'voiceCreate.sampleConfidentFemaleCorporate', prompt: 'professional female voice, American accent, confident and clear, measured pacing, corporate' },
  { labelKey: 'voiceCreate.sampleWarmFemaleElearning', prompt: 'mature female voice, American accent, warm and confident, measured pacing, good for e-learning' },
  { labelKey: 'voiceCreate.sampleGentleFemaleBedtime', prompt: 'mature female voice, American accent, soft and gentle, slow pacing, bedtime stories' },
  { labelKey: 'voiceCreate.sampleGrandmotherlyChildren', prompt: "warm grandmotherly female voice, American accent, gentle, slow pacing, children's stories" },
  { labelKey: 'voiceCreate.sampleNurturingFemaleHealthcare', prompt: 'mature female voice, American accent, warm and nurturing, calm pacing, good for healthcare' },
  { labelKey: 'voiceCreate.sampleBritishFemaleNarrator', prompt: 'mature female voice, British accent, warm and friendly, measured pacing, good for narration' },
  { labelKey: 'voiceCreate.sampleSoftFemaleGuidedMeditation', prompt: 'mature female voice, American accent, soft and warm, slow pacing, guided meditation' },
  { labelKey: 'voiceCreate.sampleProfessionalFemaleTraining', prompt: 'mature female voice, American accent, confident and professional, measured pacing, corporate training' },
  { labelKey: 'voiceCreate.sampleElegantItalianFemaleFashion', prompt: 'mature female voice, Italian accent, elegant and warm, measured pacing, fashion brand' },
  { labelKey: 'voiceCreate.sampleCalmFemaleYoga', prompt: 'calm female voice, neutral accent, soothing and gentle, slow pacing, yoga and wellness' },
  { labelKey: 'voiceCreate.sampleAuthoritativeMaleCorporate', prompt: 'mature male voice, American accent, authoritative, measured pacing, suitable for corporate training' },
  { labelKey: 'voiceCreate.samplePoshBritishMaleNarrator', prompt: 'mature male voice, British accent, posh and authoritative, measured pacing, good for narration' },
  { labelKey: 'voiceCreate.sampleFrenchAuthoritativeMale', prompt: 'mature male voice, slight French accent, authoritative, suitable for corporate training' },
  { labelKey: 'voiceCreate.sampleDeepCalmMaleMeditation', prompt: 'deep male voice, American accent, calm and reassuring, slow pacing, good for meditation' },
  { labelKey: 'voiceCreate.sampleSouthernMaleStoryteller', prompt: 'mature male voice, American Southern accent, warm and friendly, relaxed pacing, storytelling' },
  { labelKey: 'voiceCreate.sampleDeepWarmMaleAudiobook', prompt: 'mature male voice, American accent, deep and warm, measured pacing, good for audiobook' },
  { labelKey: 'voiceCreate.sampleReassuringMaleRelaxation', prompt: 'mature male voice, American accent, calm and reassuring, slow pacing, relaxation' },
  { labelKey: 'voiceCreate.sampleWiseCalmMaleStorytelling', prompt: 'mature male voice, American accent, wise and calm, slow pacing, storytelling' },
  { labelKey: 'voiceCreate.sampleReassuringFemaleRelaxation', prompt: 'mature female voice, American accent, calm and reassuring, slow pacing, good for relaxation' },
  { labelKey: 'voiceCreate.sampleWarmMaleNarrator', prompt: 'mature male voice, American accent, warm and friendly, measured pacing, good for narration' },
  { labelKey: 'voiceCreate.sampleProfessionalFemaleExplainer', prompt: 'mature female voice, American accent, professional and warm, measured pacing, explainer video' },
  { labelKey: 'voiceCreate.sampleSmoothMaleSleepMeditation', prompt: 'mature male voice, American accent, smooth and reassuring, slow pacing, sleep meditation' },
  { labelKey: 'voiceCreate.sampleConfidentMaleElearning', prompt: 'mature male voice, American accent, confident and warm, measured pacing, e-learning' },
  { labelKey: 'voiceCreate.sampleSoftFemaleRelaxation', prompt: 'soft female voice, American accent, gentle and calm, slow pacing, relaxation' },
  { labelKey: 'voiceCreate.sampleSophisticatedFrenchFemaleLuxury', prompt: 'mature female voice, French accent, warm and sophisticated, measured pacing, luxury brand' },
  { labelKey: 'voiceCreate.sampleSoothingFemaleMindfulness', prompt: 'calm female voice, American accent, warm and soothing, slow pacing, mindfulness' },
  { labelKey: 'voiceCreate.sampleWarmBritishMaleDocumentary', prompt: 'mature male voice, British accent, warm and friendly, measured pacing, documentary' },
  { labelKey: 'voiceCreate.sampleGentleFemaleChildrensAudiobook', prompt: "mature female voice, American accent, gentle and warm, calm pacing, children's audiobook" },
];

// One returned voice: audition it, NAME it, and Keep it into My voices. Kept rows lock so
// the user can keep several of the (1–3) results.
function DesignCandidateRow({ v, onKeep }: { v: V2Voice; onKeep: (v: V2Voice, name: string) => Promise<boolean> }) {
  const { t } = useTranslation('v2');
  const [name, setName] = useState(v.name || t('voiceCreate.myVoiceDefault'));
  const [saving, setSaving] = useState(false);
  const [kept, setKept] = useState(false);
  // Reflect live play/stop state so the button is a real toggle (was play-only, ran forever).
  const [, force] = useState(0);
  useEffect(() => onPreviewChange(() => force((n) => n + 1)), []);
  const playing = isPreviewPlaying(v.preview_audio_url);
  const loading = isPreviewLoading(v.preview_audio_url);
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl glass">
      <button
        onClick={() => togglePreview(v.preview_audio_url)}
        className="w-9 h-9 rounded-full bg-surface-light hover:bg-surface flex items-center justify-center text-text-primary shrink-0 transition-colors"
        title={playing || loading ? t('voiceCreate.stop') : t('voiceCreate.playPreview')}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Square className="w-3.5 h-3.5 fill-current" /> : <Play className="w-4 h-4" />}
      </button>
      <div className="flex-1 min-w-0">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={kept}
          maxLength={60}
          placeholder={t('voiceCreate.nameThisVoice')}
          className="w-full bg-transparent border-0 border-b border-glass-border focus:border-primary/50 outline-none text-sm font-semibold text-text-primary py-0.5 disabled:text-text-secondary"
        />
        <p className="text-xs text-text-tertiary mt-0.5">{[v.gender, v.language].filter(Boolean).join(' · ')}</p>
      </div>
      {kept ? (
        <span className="px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-xs font-semibold text-emerald-300 shrink-0 flex items-center gap-1">
          <Check className="w-3.5 h-3.5" /> {t('voiceCreate.kept')}
        </span>
      ) : (
        <button
          onClick={async () => { setSaving(true); const ok = await onKeep(v, name); setSaving(false); if (ok) setKept(true); }}
          disabled={saving || !name.trim()}
          className="px-3 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-xs font-semibold text-primary disabled:opacity-50 shrink-0"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('voiceCreate.keep')}
        </button>
      )}
    </div>
  );
}

export function DesignVoiceModal({ onClose, onDone, onCreated }: { onClose: () => void; onDone?: () => void; onCreated?: (v: V2Voice) => void }) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const isPaid = useUserStore((s) => s.isPro());
  const [prompt, setPrompt] = useState('');
  const [seed, setSeed] = useState(0);
  const [candidates, setCandidates] = useState<V2Voice[]>([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per the API: seed selects the result batch (0 = top matches, 1 = next, …)
  // and the same prompt+seed is deterministic. Pass the seed explicitly —
  // reading it from state here would use the stale value after setSeed.
  const design = async (useSeed: number) => {
    if (!userId || !prompt.trim()) return;
    // Premium gating: designing a voice (searching candidates) spends money, so
    // it is paid-only → redirect free users to upgrade at the CTA.
    if (!isPaid) { useUpgradeModal.getState().show({ kind: 'free_asset_limit', asset: 'voice' }); return; }
    setBusy(true); setError(null);
    try {
      const res = await v2Service.designVoice({
        userId, prompt: prompt.trim().slice(0, 1000), seed: useSeed,
      });
      setCandidates(res.candidates || []);
      setSearched(true);
    } catch {
      setError(t('voiceCreate.errDesignFailed'));
    } finally { setBusy(false); }
  };

  // Keep a candidate as a "My voice" with the user's chosen name. Does NOT close the modal,
  // so the user can keep more than one of the (1–3) returned voices; close with X when done.
  const keep = async (v: V2Voice, name: string): Promise<boolean> => {
    if (!userId) return false;
    const finalName = (name || '').trim() || v.name || t('voiceCreate.myVoiceDefault');
    try {
      await v2Service.saveDesignedVoice({
        userId, voiceId: v.voice_id, name: finalName,
        language: v.language, gender: v.gender, previewAudioUrl: v.preview_audio_url,
      });
      trackAmpVoiceCreated({ method: 'design' });
      onCreated?.({ ...v, name: finalName });
      onDone?.();
      toast.success(t('voiceCreate.toastVoiceSaved', { name: finalName }));
      return true;
    } catch (e) {
      if (isFreeLimitError(e)) { onClose(); return false; } // global upgrade popup handles it
      if (isCreditDeclined(e)) { return false; } // user cancelled the credit charge — keep modal open
      toast.error(t('voiceCreate.toastSaveFailed'));
      return false;
    }
  };

  return (
    <Modal title={t('voiceCreate.designTitle')} onClose={onClose} wide>
      <div className="space-y-3">
        <div>
          <textarea
            className={cn(inputCls, 'resize-none')}
            rows={5}
            maxLength={1000}
            placeholder={t('voiceCreate.designPromptPlaceholder')}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <p className="text-[10px] text-text-tertiary mt-1 text-right">{prompt.length}/1000</p>
        </div>
        <div className="flex gap-2">
          {/* Sample-prompt dropdown — autofills the box with a prompt known to return voices. */}
          <select
            className={cn(inputCls, 'flex-1 min-w-0')}
            value=""
            onChange={(e) => { if (e.target.value) setPrompt(e.target.value); }}
            title={t('voiceCreate.samplePickTitle')}
          >
            <option value="">{t('voiceCreate.samplePromptsOption')}</option>
            {SAMPLE_PROMPTS.map((s) => <option key={s.labelKey} value={s.prompt}>{t(s.labelKey)}</option>)}
          </select>
          <button
            onClick={() => { setSeed(0); void design(0); }}
            disabled={busy || !prompt.trim()}
            className="px-4 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold disabled:opacity-50 shrink-0"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('voiceCreate.findVoices')}
          </button>
        </div>
        {error && <p className="text-xs text-amber-300/80">{error}</p>}
        {searched && !busy && candidates.length === 0 && !error && (
          <p className="text-sm text-text-tertiary py-4 text-center leading-relaxed">
            {t('voiceCreate.noMatch')}<br />
            {t('voiceCreate.egPrefix')} <span className="text-text-secondary">{t('voiceCreate.noMatchExample')}</span>
          </p>
        )}
        {candidates.length > 0 && (
          <p className="text-xs text-text-tertiary px-1">{t('voiceCreate.candidatesHint')}</p>
        )}
        {candidates.map((v, i) => (
          <DesignCandidateRow key={`${v.voice_id}-${i}`} v={v} onKeep={keep} />
        ))}
        {candidates.length > 0 && (
          <button
            onClick={() => { const next = seed + 1; setSeed(next); void design(next); }}
            disabled={busy}
            className="w-full py-2 rounded-xl glass text-xs text-text-secondary hover:text-text-primary disabled:opacity-50"
          >
            {t('voiceCreate.showDifferentOptions')}
          </button>
        )}
      </div>
    </Modal>
  );
}
